import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { authCategories, hasPermission } from 'src/utils/has-permissions';
import logToucodeEvent, { APP_EVENTS } from 'src/utils/log-google-events';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { deleteSupplier, getPaginatedSuppliers, removeSupplier } from 'src/redux/slices/suppliers';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  ISupplierListItem,
  ISupplierTableFilters,
  ISupplierTableFilterValue,
  ISupplierTableSort,
} from 'src/types/supplier';

import SupplierTableRow from '../supplier-table-row';
import SupplierTableToolbar from '../supplier-table-toolbar';

const TABLE_HEAD = [
  { id: 'supplierName', label: 'Name', align: 'left', primary: true },
  { id: 'contactPerson', label: 'Contact Person', align: 'left', primary: true },
  { id: 'phoneNumber', label: 'Phone Number', align: 'left', primary: true },
  { id: 'email', label: 'Email Address', align: 'left', primary: true },
  { id: 'updatedAt', label: 'Updated', align: 'left', primary: true },
  { id: '', primary: true },
];

const defaultFilters: ISupplierTableFilters = {
  generalSearch: '',
};

const defaultSortBy: ISupplierTableSort = {
  sortBy: 'updatedAt',
  order: 'desc',
};

export default function SupplierListView() {
  const { status, suppliers, error } = useAppSelector((state) => state.suppliers);

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const handleFilters = useCallback(
    (name: string, value: ISupplierTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.suppliers.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedSuppliers = useCallback(() => {
    const authorizedCategories = authCategories(authUser);

    if (authorizedCategories.length > 0) {
      dispatch(
        getPaginatedSuppliers({
          page,
          limit: rowsPerPage,
          generalSearch: filters.generalSearch,
          sort: sort.order,
          sortBy: sort.sortBy,
          categories: authorizedCategories,
          categoriesSeparator: '|',
        })
      );
    }
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters, authUser]);

  const handleDeleteRow = useCallback(
    async (supplier: ISupplierListItem, index: number) => {
      dispatch(removeSupplier({ supplierId: supplier._id }));

      try {
        await dispatch(deleteSupplier({ supplier, index })).unwrap();

        fetchPaginatedSuppliers();
        enqueueSnackbar('Supplier deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedSuppliers]
  );

  useEffect(() => {
    fetchPaginatedSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPaginatedSuppliers]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Suppliers"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_SUPPLIER,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.suppliers.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Supplier
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <SupplierTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD.filter(({ primary }) => {
                  if (settings.themeLayout === 'vertical') {
                    return primary;
                  }
                  return true;
                })}
                rowCount={suppliers.suppliers.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {suppliers?.suppliers.map((row: ISupplierListItem, index: number) => (
                      <SupplierTableRow
                        key={row._id}
                        row={row}
                        selected={table.selected.includes(row._id)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && suppliers.count === 0 && (
                  <TableNoData message="No suppliers found" notFound={suppliers.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={suppliers.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={(value) => {
            table.onChangeDense(value);

            logToucodeEvent(APP_EVENTS.TABLE_COMPACT_MODE_TOGGLE, {
              compact: value,
              table: 'suppliers_table',
            });
          }}
        />
      </Card>
    </Container>
  );
}
